import { atom } from 'jotai';
import { atomFamily, atomWithReset } from 'jotai/utils';

// Note: Scene ID is a string concatenation of log_name and timestamp

/** User-configurable duration (in seconds) for the overlap check. Defaults to 3. */
export const overlapDurationAtom = atomWithReset(3);

/** User-configurable sampling frequency (in Hz) for the overlap check. Defaults to 2. */
export const overlapHzAtom = atomWithReset(2);

/** User-selected pipeline token for the overlap check. */
export const overlapPipelineTokenAtom = atomWithReset<string | null>(null);

/** A simple counter that increments whenever the overlap settings (duration, hz, pipelineToken) change.
 * This atom is "read" by overlapStatusFamily, to force a re-evaluation of all overlap statuses when the user changes global overlap settings.
 */
export const overlapSettingsVersionAtom = atom(0);

/** FOR DEBUGGING: Allows manually forcing all items into a specific overlap state for testing. */
export const debugOverrideOverlapAtom = atom<
  'off' | 'loading' | 'is_overlapping' | 'error'
>('off');

/** Returns true if any of the overlap options are not set to their default values. */
export const isOverlapOptionsActiveAtom = atom((get) => {
  const duration = get(overlapDurationAtom);
  const hz = get(overlapHzAtom);
  const pipelineToken = get(overlapPipelineTokenAtom);

  return duration !== 3 || hz !== 2 || pipelineToken !== null;
});

/**
 * Stores the overlap status for each individual scene.
 * The atom family is indexed by the sceneId (log_name + timestamp).
 * Status can be:
 * - 'idle': The check has not been performed yet.
 * - 'loading': The check is in progress.
 * - true: The scene is overlapping.
 * - false: The scene is not overlapping.
 * - 'error': The check failed.
 */
export const overlapStatusFamily = atomFamily((_sceneId: string) =>
  atom<'idle' | 'loading' | true | false | 'error'>('idle')
);
